import React from 'react';

import type { MarkAnimation } from './animations';

interface RapidSOSMarkProps {
  className?: string;
  animation?: MarkAnimation;
}

// [cx, cy, column, row] for each dot of the RapidSOS mark (viewBox 0 0 38 38).
const DOTS: ReadonlyArray<readonly [number, number, number, number]> = [
  [13.9251, 3.80774, 1, 0],
  [24.0513, 3.80774, 2, 0],
  [3.79545, 13.9698, 0, 1],
  [13.9253, 13.9698, 1, 1],
  [24.0513, 13.9698, 2, 1],
  [34.1744, 13.9698, 3, 1],
  [3.79545, 24.1281, 0, 2],
  [13.9253, 24.1281, 1, 2],
  [24.0513, 24.1281, 2, 2],
  [34.1744, 24.1281, 3, 2],
  [13.9253, 34.2882, 1, 3],
  [24.0513, 34.2882, 2, 3],
];

// Rainbow palette indexed by diagonal (column + row). The brand green sits in
// the middle of the sweep so it flashes through the dance.
const DISCO_COLORS = ['#ff3b3b', '#ff9f1c', '#ffd23f', '#00f500', '#2ec4ff', '#a855f7'];

// Geometric center of the mark (viewBox is 0..38 on both axes).
const CENTER = 19;

const DISCO_STEP_MS = 90;
const ROUTE_STEP_MS = 80;
const VORTEX_STEP_MS = 70;
const RIPPLE_STEP_MS = 120;
// Matrix: each column starts as its own stream (col), cascading down (row).
const RAIN_COL_STEP_MS = 110;
const RAIN_ROW_STEP_MS = 70;
const GLITCH_STEP_MS = 45;

// Per-dot horizontal jitter (px) for the glitch stutter; alternating signs.
const GLITCH_OFFSETS = [5, -6, 4, -5, 6, -4, 5, -6, 3, -5, 6, -4];

function dotStyle(
  animation: MarkAnimation | undefined,
  cx: number,
  cy: number,
  col: number,
  row: number,
  index: number,
): React.CSSProperties | undefined {
  const diagonal = col + row;
  // Manhattan distance of the dot's grid cell from the center of the grid.
  const ring = Math.abs(col - 1.5) + Math.abs(row - 1.5);

  switch (animation) {
    case 'disco':
      return {
        '--disco': DISCO_COLORS[diagonal],
        animationDelay: `${diagonal * DISCO_STEP_MS}ms`,
      } as React.CSSProperties;

    case 'route':
      // Offset that places the dot at the center initially; it animates back to 0.
      return {
        '--dx': `${CENTER - cx}px`,
        '--dy': `${CENTER - cy}px`,
        animationDelay: `${(ring - 1) * ROUTE_STEP_MS}ms`,
      } as React.CSSProperties;

    case 'vortex':
      return {
        '--disco': DISCO_COLORS[diagonal],
        animationDelay: `${(ring - 1) * VORTEX_STEP_MS}ms`,
      } as React.CSSProperties;

    case 'rain':
      // Start above the mark; column-staggered so it falls left → right.
      return {
        '--drop': `${-(cy + 10)}px`,
        animationDelay: `${col * RAIN_COL_STEP_MS + row * RAIN_ROW_STEP_MS}ms`,
      } as React.CSSProperties;

    case 'ripple':
      return {
        '--disco': DISCO_COLORS[diagonal],
        animationDelay: `${(ring - 1) * RIPPLE_STEP_MS}ms`,
      } as React.CSSProperties;

    case 'glitch':
      return {
        '--gx': `${GLITCH_OFFSETS[index % GLITCH_OFFSETS.length]}px`,
        animationDelay: `${index * GLITCH_STEP_MS}ms`,
      } as React.CSSProperties;

    default:
      return undefined;
  }
}

export const RapidSOSMark: React.FC<RapidSOSMarkProps> = ({ className, animation }) => {
  const classes = [animation ? `rapidsos-mark--${animation}` : null, className]
    .filter(Boolean)
    .join(' ');

  return (
    <svg
      viewBox="0 0 38 38"
      className={classes || undefined}
      style={{ overflow: 'visible' }}
      role="img"
      aria-label="RapidSOS"
      fill="none"
    >
      {DOTS.map(([cx, cy, col, row], i) => (
        <circle
          key={i}
          cx={cx}
          cy={cy}
          r={3.8}
          fill="#FAFAFA"
          className={animation ? 'rapidsos-mark__dot' : undefined}
          style={dotStyle(animation, cx, cy, col, row, i)}
        />
      ))}
    </svg>
  );
};
